/**
 * Print helper using iframe isolation.
 * Safari's print renderer has issues with visibility:hidden/visible CSS tricks
 * used to isolate print content in the main document. By printing from an iframe
 * that contains ONLY the content to print, we avoid all cross-browser CSS issues.
 */

/**
 * Extract readable CSS rules from the current document, EXCLUDING @media print
 * rules. The main app's @media print rules are designed for main-window printing
 * (hiding everything except .receipt via position:absolute). In an iframe that
 * contains ONLY the receipt content, those rules are harmful — they prevent
 * multi-page flow because position:absolute clips to one page.
 *
 * Cross-origin stylesheets (e.g. FontAwesome CDN) are skipped since their
 * cssRules cannot be read due to CORS restrictions.
 */
function getDocumentStyles() {
    let css = '';
    for (let i = 0; i < document.styleSheets.length; i++) {
        try {
            const rules = document.styleSheets[i].cssRules;
            for (let j = 0; j < rules.length; j++) {
                const rule = rules[j];
                // Skip @media print rules — we provide our own clean print CSS
                if (rule.type === CSSRule.MEDIA_RULE && rule.conditionText && rule.conditionText.indexOf('print') !== -1) {
                    continue;
                }
                css += rule.cssText + '\n';
            }
        } catch (e) {
            // Cross-origin stylesheet - skip
        }
    }
    return css;
}

/**
 * Create a hidden iframe holding a complete document, print it, then clean up.
 * Shared by printViaIframe (A4 documents) and printLabelViaIframe (label rolls).
 *
 * @param {string} bodyHtml - The HTML to place inside <body>
 * @param {string} css - The complete CSS for the print document
 * @param {object} options
 * @param {string} options.width - Width of the offscreen iframe element
 * @param {string} options.height - Height of the offscreen iframe element
 * @param {boolean} options.waitForImages - Hold the print dialog until <img> elements have loaded
 */
function printDocumentInIframe(bodyHtml, css, options = {}) {
    const { width = '210mm', height = '297mm', waitForImages = false } = options;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.left = '-9999px';
    iframe.style.top = '-9999px';
    iframe.style.width = width;
    iframe.style.height = height;
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument;
    doc.open();
    doc.write(`<!DOCTYPE html>
<html lang="en" data-bs-theme="light">
<head>
<meta charset="utf-8">
<style>
${css}
</style>
</head>
<body class="bootstrap" data-bs-theme="light">
${bodyHtml}
</body>
</html>`);
    doc.close();

    // Clean up iframe after printing completes
    iframe.contentWindow.onafterprint = () => {
        if (iframe.parentNode) {
            document.body.removeChild(iframe);
        }
    };
    const doPrint = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        // Fallback cleanup if onafterprint doesn't fire (e.g. Safari)
        setTimeout(() => {
            if (iframe.parentNode) {
                document.body.removeChild(iframe);
            }
        }, 5000);
    };

    // Wait for iframe content to fully render before printing.
    setTimeout(() => {
        const pending = waitForImages
            ? Array.prototype.slice.call(doc.images || []).filter(img => !img.complete)
            : [];
        if (!pending.length) {
            doPrint();
            return;
        }

        // A logo that has not decoded yet prints as an empty box, so hold the
        // dialog until every image settles — but never longer than 3s, a broken
        // image URL must not block printing entirely.
        let printed = false;
        const printOnce = () => {
            if (printed) return;
            printed = true;
            doPrint();
        };
        let remaining = pending.length;
        const onSettled = () => {
            remaining -= 1;
            if (remaining <= 0) printOnce();
        };
        pending.forEach(img => {
            img.addEventListener('load', onSettled, { once: true });
            img.addEventListener('error', onSettled, { once: true });
        });
        setTimeout(printOnce, 3000);
    }, 250);
}

/**
 * Print HTML content via a hidden iframe.
 * Creates an iframe with the document's styles (minus @media print rules)
 * plus clean print CSS, then triggers print from the iframe's window.
 *
 * @param {string} htmlContent - The HTML to print
 * @param {object} options
 * @param {string} options.customCss - Additional CSS to inject
 * @param {boolean} options.waitForImages - Hold the print dialog until <img> elements have loaded
 */
export function printViaIframe(htmlContent, options = {}) {
    const { customCss = '', waitForImages = false } = options;
    const documentStyles = getDocumentStyles();

    printDocumentInIframe(htmlContent, `${documentStyles}
${customCss}
/* Clean print CSS for iframe-based printing.
   Since the iframe ONLY contains the content to print, we don't need
   to hide/show elements — everything should be visible and flow naturally
   across multiple pages. */
@media print {
    @page {
        margin: 10mm;
    }
    html, body {
        height: auto !important;
        max-height: none !important;
        overflow: visible !important;
    }
    *, *::before, *::after {
        visibility: visible !important;
    }
    .receipt, .printPrescription, .print-section {
        position: static !important;
        width: 100% !important;
        padding: 5mm !important;
        box-sizing: border-box !important;
        font-size: 9pt;
        color: black !important;
        background: white !important;
    }
    .receipt .receipt {
        padding: 0 !important;
    }
    .table-responsive {
        overflow: visible !important;
        max-height: none !important;
    }
    table {
        page-break-inside: auto !important;
    }
    tr {
        page-break-inside: avoid !important;
    }
}
/* Override the main app's html,body { height:100%; overflow:hidden }
   which gets copied from custom.css line 2-16 via getDocumentStyles().
   Without this, content beyond one viewport is clipped and cannot
   flow to additional print pages. */
html, body {
    height: auto !important;
    max-height: none !important;
    overflow: visible !important;
    margin: 0 !important;
    padding: 0 !important;
    background: white !important;
    color: black !important;
    color-scheme: light !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
}`, { waitForImages });
}

/**
 * Print a LABEL via a hidden iframe.
 *
 * Label printers (Brother QL-xxx, Dymo, ...) feed a sheet that IS the label, so
 * the printed page must be the label and nothing else:
 *   - `@page { margin: 0 }` — printViaIframe's 10mm document margin would eat
 *     10mm on every side of a 29mm tall label.
 *   - `@page { size: auto }` — keep whatever media the user picked in the print
 *     dialog (62x29mm, 60x86mm, 62x100mm ...) instead of forcing one roll.
 *   - `.printLabel` is 100% x 100% of that page, so the same markup fills the
 *     label whichever roll is loaded.
 *
 * The app's own stylesheets are deliberately NOT copied in here: label markup is
 * self-contained, and the app CSS (html/body height:100%;overflow:hidden, svg
 * vertical-align, dark-mode colors, web fonts) only distorts it.
 *
 * @param {string} htmlContent - The label HTML, root element having class printLabel
 * @param {object} options
 * @param {string} options.customCss - Additional CSS to inject
 */
export function printLabelViaIframe(htmlContent, options = {}) {
    const { customCss = '' } = options;

    printDocumentInIframe(htmlContent, `
@page {
    size: auto;
    margin: 0;
}
html, body {
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    overflow: hidden;
    background: #ffffff;
    color: #000000;
    color-scheme: light;
    font-family: Arial, Helvetica, sans-serif;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
}
/* The label root: fills the whole physical label, never spills to page 2. */
.printLabel {
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    margin: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    color: #000000;
    background: #ffffff;
    break-inside: avoid;
    page-break-inside: avoid;
    page-break-after: avoid;
}
/* Flex children must be allowed to shrink below their intrinsic size, otherwise
   a tall barcode pushes the text rows off the label. */
.printLabel > * {
    min-width: 0;
    min-height: 0;
}
.printLabel svg {
    display: block;
}
/* A logo on a label must never push the text off the sheet. */
.printLabel img {
    display: block;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

/* A label made of a fixed-aspect graphic (logo, QR code) plus a text body.
   Stacked on a portrait roll, side by side on a landscape one — a square QR or
   a wide logo would otherwise leave half the label empty. In paged media the
   aspect ratio is the page box, i.e. the label itself.

   .printLabel-media needs a definite basis: sizing it from the graphic's own
   aspect ratio would make the flex item's width depend on its height, and the
   browser resolves that circular case by collapsing it to nothing. */
.printLabel-split {
    flex-direction: column;
}
.printLabel-media {
    flex: 0 0 50%;
    min-width: 0;
    min-height: 0;
    margin-bottom: 1mm;
}
.printLabel-media img {
    width: 100%;
    height: 100%;
    object-position: left center;
}
.printLabel-body {
    flex: 1 1 0;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
}
@media (min-aspect-ratio: 1/1) {
    .printLabel-split {
        flex-direction: row;
    }
    .printLabel-media {
        flex: 0 0 35%;
        margin-right: 2mm;
        margin-bottom: 0;
    }
}
${customCss}`, { width: '80mm', height: '120mm', waitForImages: true });
}
